import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Box, Typography } from '@mui/material'
import FunnelGraph, { FunnelGraphData } from 'd3-funnel-graph'
import 'd3-funnel-graph/dist/css/funnel-graph.min.css'

import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { Button } from '../../../interaxDesignSystemComponents'

const SAMPLE_DATA: FunnelGraphData = {
  labels: ['Loop 1', 'Loop 2', 'Loop 3'],
  subLabels: [],
  colors: [
    ['#FFB178', '#FF78B1', '#FF3C8E'],
    ['#A0BBFF', '#EC77FF'],
    ['#A0F9FF', '#7795FF']
  ],
  values: [
    [3000, 2500, 6500],
    [3000, 1700, 1000],
    [600, 200, 130]
  ]
}

/**
 * Dev playground that mirrors the official FunnelGraph example so we can compare styling
 * against the loops/sections data we plan to display inside SessionCard.
 */
export const FunnelGraphDevTest: React.FC = () => {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const graphRef = useRef<FunnelGraph | null>(null)

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    // Clean up any previous instance before drawing again
    graphRef.current?.destroy()

    const graph = new FunnelGraph({
      container: containerRef.current,
      data: SAMPLE_DATA,
      direction: 'horizontal',
      gradientDirection: 'horizontal',
      displayPercent: false,
      width: containerRef.current.clientWidth || 800,
      height: 320,
      subLabelValue: 'raw',
      margin: {
        top: 120,
        right: 60,
        bottom: 60,
        left: 60,
        text: {
          left: 10,
          top: 0
        }
      },
      resize: false,
      responsive: false,
      responsiveWidth: false,
      responsiveHeight: false
    })

    graph.draw()
    graphRef.current = graph

    return () => {
      graph.destroy()
      graphRef.current = null
    }
  }, [])

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <Box sx={{ padding: '24px', backgroundColor: INTERAX_COLORS.offBlack, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <Button variant="secondary" onClick={handleBack} icon={<ArrowLeftIcon className="w-4 h-4" />} />
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.headline1,
            color: INTERAX_COLORS.white
          }}
        >
          Funnel Graph Prototype
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: 980 }}>
        <Typography sx={{ ...INTERAX_TYPOGRAPHY.body1, color: INTERAX_COLORS.thunderGrey }}>
          This screen reproduces the vanilla <code>d3-funnel-graph</code> demo to compare the native horizontal layout
          against our centered stacked airtime visualization. Each label corresponds to a loop (Loop 1-3) and displays
          raw player totals with gradient fills per section.
        </Typography>

        <Box
          sx={{
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '16px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            padding: { xs: '16px', md: '32px' }
          }}
        >
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.headline3,
              color: INTERAX_COLORS.white,
              marginBottom: '16px'
            }}
          >
            Horizontal Funnel Example
          </Typography>

          <Box
            ref={containerRef}
            sx={{
              width: '100%',
              height: { xs: 320, md: 360 },
              '& .funnel': {
                width: '100%',
                height: '100%'
              }
            }}
            className="funnel-wrapper"
          />
        </Box>
      </Box>
    </Box>
  )
}

export default FunnelGraphDevTest
